/**
 * Import dependencies
 */
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _x, sprintf } from '@wordpress/i18n';

// Import style for web component
import styles from './web-component-style.scss?inline';

/**
 * Object containing status data with their corresponding labels, icons, and CSS color variables.
 * @type {Object<String, {label: String, icon: String, colorVariable: String}>}
 */
const statusesData = {
	available: {
		label: escapeHTML( _x( 'Available', 'Feature status', 'uncanny-automator' ) ),
		icon: 'check-circle',
		colorVariable: '--uap-page-upgrade-table-status-completed',
	},
	unavailable: {
		label: escapeHTML( _x( 'Unavailable', 'Feature status', 'uncanny-automator' ) ),
		icon: 'hyphen',
		colorVariable: '--uap-page-upgrade-table-status-unavailable',
	},
	limited: {
		label: escapeHTML( _x( 'Limited', 'Feature status', 'uncanny-automator' ) ),
		icon: 'circle-exclamation',
		colorVariable: '--uap-page-upgrade-table-status-limited',
	}
};

/**
 * A custom web component to display a feature status with icons, labels, and optional tooltips.
 * uap-upgrade-table-status
 * @augments LitElement
 */
@customElement( 'uap-page-upgrade-table-status' ) // eslint-disable-line require-jsdoc
export class AutomatorUpgradeTableStatus extends LitElement {
	/**
	 * Component styles loaded from an external SCSS file.
	 * @static
	 * @type {CSSResult}
	 */
	static styles = css`${unsafeCSS( styles )}`;

	/**
	 * Status ID to determine which status data to display.
	 * @type {String}
	 */
	@property( { type: String, attribute: 'status-id' } )
	accessor statusID;

	/**
	 * Whether to hide the status label.
	 * @type {Boolean}
	 * @default true
	 */
	@property( { type: Boolean, attribute: 'hide-label' } )
	accessor hideLabel = true;

	/**
	 * Whether to show a tooltip for the status component.
	 * @type {Boolean}
	 * @default true
	 */
	@property( { type: Boolean, attribute: 'show-tooltip' } )
	accessor showTooltip = true;

	/**
	 * Renders the status component.
	 *
	 * If an invalid status ID is provided, an error message will be displayed.
	 * If tooltips are enabled, the status is rendered inside a tooltip component.
	 *
	 * @return {TemplateResult} The rendered HTML template for the component.
	 */
	render() {
		// Check that the user is using a valid ID
		if ( ! isDefined( statusesData[ this.statusID ] ) ) {
			return html`${ sprintf(
				/* translators: 1. The status ID */
				escapeHTML( __( 'Error: "%1$s" is not a valid status ID.', 'uncanny-automator' ) ),
				this.statusID
			) }`;
		}

		// Render status without tooltip
		if ( ! this.showTooltip ) {
			return this._templateStatus;
		}

		// Render status with tooltip
		return html`
			<uo-tooltip>
				<span class="tooltip-nowrap">${ statusesData[ this.statusID ].label }</span>
				<div slot="target">${ this._templateStatus }</div>
			</uo-tooltip>
		`;
	}

	/**
	 * Generates a template for the status component.
	 *
	 * This includes a status icon with a dynamic color, and optionally a label if `hideLabel` is false.
	 *
	 * @private
	 * @return {TemplateResult} The HTML template for the status.
	 */
	get _templateStatus() {
		return html`
			<div class="status">
				<span 
					class="status__icon" 
					style="color: var(${ statusesData[ this.statusID ].colorVariable })"
				>
					<uo-icon 
						id="${ statusesData[ this.statusID ].icon }"
						icon-style="solid"
					></uo-icon>
				</span>

				${ this._templateLabel }
			</div>
		`;
	}

	/**
	 * Generates a template for the status label.
	 * This is only rendered if `hideLabel` is false.
	 *
	 * @private
	 * @return {TemplateResult|undefined} The HTML template for the label or undefined if hidden.
	 */
	get _templateLabel() {
		// Check if the label should be hidden
		if ( this.hideLabel ) {
			return;
		}

		return html`<span class="status__label">${ statusesData[ this.statusID ].label }</span>`;
	}
}
